import type { PropostaItem } from '@/types'
import { recurringSetupEntry } from '@/utils/recurringSetupEntry'
import {
  categoryMatchKey,
  defaultLayoutConfig,
  extractServices,
} from '@/utils/dealCalculations'
import type { LayoutConfig } from '@/utils/dealCalculations'
import { normalizeInitialLayout } from '@/components/Deals/ProposalStudio/normalizeInitialLayout'
import {
  getBuiltinPlanCategoryCoords,
  getBuiltinPlanSlideLayout,
} from '@/utils/planLayoutDefaults'
import {
  formatCategoryDisplayTitle,
  normalizeObjectEncoding,
  normalizeTextEncoding,
} from '@/utils/text-normalization'
import type { ScopeCategoryInput } from '@/components/Deals/ProposalStudio/layoutSlideBlocks'
import {
  findCatalogPlanForItem,
  getProposalCategoryCoordsFromCatalog,
  getProposalLayoutDefaultsFromCatalog,
  getScopeContentSignature,
  isGalaxyPlanLabel,
  mergeCategoryCoordMaps,
  remapCategoryCoordsToScope,
  resolveCategoryCoordsForScope,
  type CatalogPlan,
} from '@/utils/planCatalogCore'
import { pickSavedSlidePositions } from '@/utils/savedSlidePositions'
import { visibleProjectTypeLabel } from '@/utils/slideProjectTypeLabel'

/** Fingerprint do catálogo para re-sincronizar quando um plano é editado em Config. */
export function getCatalogFingerprint(catalog: CatalogPlan[]): string {
  return catalog
    .map((p) => {
      const scope = getScopeFromCatalog(p)
      const layout = getProposalCategoryCoordsFromCatalog(p)
      return `${p.id}:${getScopeContentSignature(scope)}:${JSON.stringify(layout || {})}`
    })
    .join('|')
}

export function hasProposalScope(item: PropostaItem): boolean {
  return Array.isArray(item.services) && item.services.length > 0
}

function mapSavedScopeToCategories(services: PropostaItem['services']): ScopeCategoryInput[] {
  if (!Array.isArray(services)) return []
  return services.map((cat) => {
    const title = formatCategoryDisplayTitle((cat.category || cat.categoria || 'Geral').trim())
    return {
      category: title,
      categoria: title,
      services: (cat.services || []).map((svc: { name?: string; nome?: string; isIncluded?: boolean; isPartial?: boolean; quantity?: string | number; quantidade?: string | number }) => ({
        ...svc,
        name: normalizeTextEncoding(svc.name || (svc as { nome?: string }).nome || ''),
        nome: normalizeTextEncoding(svc.name || (svc as { nome?: string }).nome || ''),
      })),
    }
  })
}

export function countScopeItems(scope: ScopeCategoryInput[]): number {
  return scope.reduce(
    (sum, cat) =>
      sum + (cat.services || []).filter((s) => s.isIncluded !== false).length,
    0,
  )
}

const GENERIC_SCOPE_CATEGORY_KEYS = new Set([
  'marketing',
  'recorrente',
  'geral',
  'planos',
  'servicos',
  'serviços',
  'outros',
])

/** Planos com um único bloco e muitos itens por design (ex.: Suporte e Evolução). */
const INTENTIONAL_SINGLE_CATEGORY_KEYS = new Set(['servicos disponiveis'])

/**
 * Escopo “achatado” (ex.: 13 itens num único bloco “Marketing”) — usar catálogo.
 */
export function isDegenerateProposalScope(
  scope: ScopeCategoryInput[],
  catalogScope?: ScopeCategoryInput[],
): boolean {
  const items = countScopeItems(scope)
  if (items === 0) return false

  const firstKey = categoryMatchKey(scope[0]?.category || scope[0]?.categoria || '')
  if (INTENTIONAL_SINGLE_CATEGORY_KEYS.has(firstKey)) return false

  const genericBucket =
    scope.length === 1 && GENERIC_SCOPE_CATEGORY_KEYS.has(firstKey) && items >= 4

  if (genericBucket || (scope.length === 1 && items > 6)) return true

  if (catalogScope && catalogScope.length >= 2) {
    const catalogCats = catalogScope.length
    const catalogItems = countScopeItems(catalogScope)
    if (scope.length === 1 && catalogCats >= 2 && items >= Math.min(catalogItems, 8)) return true
    if (scope.length < catalogCats && items >= catalogItems) return true
  }

  return false
}

export function isDegenerateProposalScopeFromItem(
  item: PropostaItem,
  catalogScope: ScopeCategoryInput[],
): boolean {
  if (!hasProposalScope(item)) return false
  return isDegenerateProposalScope(mapSavedScopeToCategories(item.services), catalogScope)
}

/**
 * Escopo efetivo: proposta salva, exceto escopo degenerado → catálogo (3 grupos no EVOLVE).
 */
export function resolveProposalItemScope(
  item: PropostaItem,
  catalog: CatalogPlan[] = [],
): ScopeCategoryInput[] {
  if (hasProposalScope(item)) {
    const fromProposal = mapSavedScopeToCategories(item.services)
    const master = catalog.length ? findCatalogPlanForItem(item, catalog) : undefined
    if (master) {
      const fromCatalog = getScopeFromCatalog(master) as ScopeCategoryInput[]
      if (isDegenerateProposalScope(fromProposal, fromCatalog)) {
        return fromCatalog
      }
    }
    return fromProposal
  }
  const master = catalog.length ? findCatalogPlanForItem(item, catalog) : undefined
  if (master) return getScopeFromCatalog(master) as ScopeCategoryInput[]
  return extractServices(item) as ScopeCategoryInput[]
}

/** Renomeia chave em categoryCoords (match case-insensitive). */
export function renameCategoryCoordKey(
  coords: LayoutConfig['categoryCoords'] | undefined,
  oldName: string,
  newName: string,
): LayoutConfig['categoryCoords'] {
  if (!coords || !newName.trim()) return coords || {}
  const trimmed = normalizeTextEncoding(newName.trim())
  const result = { ...coords }
  const matchKey = Object.keys(coords).find((k) => categoryMatchKey(k) === categoryMatchKey(oldName))
  if (matchKey) {
    result[trimmed] = coords[matchKey]
    if (matchKey !== trimmed) delete result[matchKey]
  }
  return result
}

/** Escopo (services) do plano mestre no catálogo (Config → Planos). */
export function getScopeFromCatalog(master: CatalogPlan): ReturnType<typeof extractServices> {
  return extractServices({
    id: master.id,
    name: master.nome,
    config: master.config,
    services: master.config?.services,
    category: master.category,
  })
}

export interface SyncCatalogOptions {
  /** Mantém valores comerciais já negociados na proposta (preço, parcelas). */
  preserveCommercial?: boolean
  /** Substitui escopo salvo pelo catálogo (botão "Atualizar escopo"). */
  forceScopeSync?: boolean
}

/**
 * Monta item de proposta a partir do plano mestre.
 * Usado ao abrir o studio, ao adicionar plano e ao exportar preview.
 */
export function syncProposalItemFromCatalog(
  item: PropostaItem,
  master: CatalogPlan,
  options: SyncCatalogOptions = {},
): PropostaItem {
  const { preserveCommercial = false, forceScopeSync = false } = options
  const scope = getScopeFromCatalog(master)
  const keepSavedScope = preserveCommercial && hasProposalScope(item) && !forceScopeSync
  const cfg = master.config || {}
  const masterPricing = (cfg.pricing as Record<string, number>) || {}
  const duration = Number(cfg.duration) || item.duration || 12
  const isRecorrente = true
  const baseVal = Number(master.valor) || item.valorUnitario || 0

  const galaxy = isGalaxyPlanLabel(master.nome)
  const supportPricingMode =
    cfg.supportPricingMode === 'BASE_500_MAIS_1_FATURAMENTO'
      ? 'BASE_500_MAIS_1_FATURAMENTO'
      : 'FIXO_2000'

  const savedName = preserveCommercial
    ? String(item.descricao || item.nome || item.name || '').trim()
    : ''
  const planName = savedName || master.nome
  const savedDescription = preserveCommercial ? String(item.description || '').trim() : ''
  const catalogDescription = String(master.descricaoPlano || '').trim()

  const copy: PropostaItem = {
    ...normalizeObjectEncoding({ ...item }),
    catalogPlanId: master.id,
    descricao: planName,
    nome: planName,
    name: planName,
    description: savedDescription || catalogDescription,
    tipo: 'plano',
    services: keepSavedScope ? (item.services as PropostaItem['services']) : scope,
    projectType: galaxy
      ? ''
      : visibleProjectTypeLabel(planName, String(cfg.projectType || ''))
        || ((planName || master.nome).toLowerCase().includes('desenvolvimento')
          ? 'Plano de Desenvolvimento'
          : ''),
    showBonus:
      preserveCommercial && typeof item.showBonus === 'boolean'
        ? item.showBonus
        : cfg.showBonus === true,
    bonus: normalizeTextEncoding(String(cfg.bonus || '')),
    duration: duration > 0 ? duration : 12,
  }

  if (!preserveCommercial) {
    const supportHours = Number(cfg.totalHours ?? 0)
    const hourlyRate = Number(cfg.hourlyRate ?? 0)
    const durationMonths = copy.duration ?? 12
    copy.pricing = {
      total: Number(masterPricing.total || baseVal * durationMonths),
      entry: Number(masterPricing.entry || 0),
      installments: Number(masterPricing.installments || (isRecorrente ? durationMonths : 1)),
      installmentValue: Number(masterPricing.installmentValue || baseVal),
      recorrente: isRecorrente,
      supportPricingMode,
      monthlyFixedValue: Number(cfg.monthlyFixedValue ?? 2000),
      monthlyBaseValue: Number(cfg.monthlyBaseValue ?? 500),
      revenueSharePercent: Number(cfg.revenueSharePercent ?? 1),
      ...(supportHours > 0 && hourlyRate > 0 ? { supportHours, hourlyRate } : {}),
    }
    copy.pricing.entry = recurringSetupEntry(copy.pricing)
    copy.valorUnitario = copy.pricing.recorrente
      ? copy.pricing.installmentValue
      : copy.pricing.total
    copy.total =
      (copy.pricing.recorrente
        ? copy.pricing.entry + durationMonths * copy.pricing.installmentValue
        : copy.pricing.total) * (copy.quantidade || 1)
  }

  return normalizeObjectEncoding(copy)
}

export function buildProposalItemFromCatalog(master: CatalogPlan): PropostaItem {
  return syncProposalItemFromCatalog(
    {
      id: '',
      descricao: master.nome,
      tipo: master.tipo,
      quantidade: 1,
      valorUnitario: master.valor,
      total: master.valor,
    },
    master,
    { preserveCommercial: false },
  )
}

/** Garante catalogPlanId e escopo resolvido (proposta salva > catálogo). */
export function overlayCatalogScopeOnItem(
  item: PropostaItem,
  catalog: CatalogPlan[],
): PropostaItem {
  const master = findCatalogPlanForItem(item, catalog)
  const scope = resolveProposalItemScope(item, catalog)
  return {
    ...item,
    catalogPlanId: master?.id ?? item.catalogPlanId,
    description: item.description ?? master?.descricaoPlano,
    services: scope,
  }
}

/**
 * Layout do slide isolado por item — catálogo/builtin do plano ativo,
 * sem reutilizar categoryCoords de outro plano no preview ou export.
 */
export function buildSlideLayoutForProposalItem(
  item: PropostaItem,
  catalogPlans: CatalogPlan[],
  savedLayout?: Partial<LayoutConfig>,
): LayoutConfig {
  const master = findCatalogPlanForItem(item, catalogPlans)
  const scope = resolveProposalItemScope(item, catalogPlans)
  const planLabel = normalizeTextEncoding(
    item.descricao ||
      item.nome ||
      (item as PropostaItem & { name?: string }).name ||
      master?.nome ||
      '',
  )

  const catalogDefaults = master ? getProposalLayoutDefaultsFromCatalog(master) : {}
  const builtinSlide = getBuiltinPlanSlideLayout(planLabel)
  const builtinCoords = getBuiltinPlanCategoryCoords(planLabel)
  const mergedPlanLayout: Partial<LayoutConfig> = {
    ...builtinSlide,
    ...catalogDefaults,
    categoryCoords: mergeCategoryCoordMaps(
      builtinCoords,
      builtinSlide?.categoryCoords,
      catalogDefaults.categoryCoords,
    ),
  }

  const { categoryCoords: planCategoryDefaults, ...slideDefaults } = mergedPlanLayout
  const baseSlide = normalizeInitialLayout({
    ...defaultLayoutConfig,
    ...slideDefaults,
  })
  const savedForScope = savedLayout
    ? remapCategoryCoordsToScope(scope, savedLayout.categoryCoords)
    : undefined

  return normalizeInitialLayout({
    ...baseSlide,
    ...pickSavedSlidePositions(savedLayout, planLabel),
    categoryCoords: resolveCategoryCoordsForScope(scope, baseSlide, {
      planDefaults: planCategoryDefaults,
      existing: savedForScope,
    }),
  }) as LayoutConfig
}
